import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, card } from '../theme'

function PositionSizingMini({ baseline, perTradeNum, onNavigate }) {
  const [credit, setCredit] = useState('');
  const [width,  setWidth]  = useState('');
  const creditNum     = parseFloat(credit) || 0;
  const widthNum      = parseFloat(width)  || 0;
  const baselineNum   = parseFloat(baseline) || 0;
  const maxLossDollar = baselineNum * perTradeNum / 100;
  const maxContracts  = widthNum > 0 ? Math.floor(maxLossDollar / (widthNum * 100)) : 0;
  const totalPremium  = maxContracts > 0 && creditNum > 0 ? maxContracts * creditNum * 100 : 0;
  const actualRisk    = maxContracts > 0 && widthNum > 0 ? (maxContracts * widthNum * 100) - totalPremium : 0;
  const inp = { width: '100%', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px', padding: '10px 14px', color: V.text, fontSize: '14px', fontFamily: "'Jost',sans-serif", outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '6px' };
  return (
    <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '22px 24px', marginBottom: '18px' }}>
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, marginBottom: '16px' }}>Position Sizing — Quick Calculator</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div>
          <div style={lbl}>Credit Received (per share, $)</div>
          <input style={inp} type="number" value={credit} onChange={e => setCredit(e.target.value)} placeholder="e.g. 1.50" step="0.05" />
        </div>
        <div>
          <div style={lbl}>Spread Width ($, 0 if naked)</div>
          <input style={inp} type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="e.g. 5 for a $5-wide spread" />
        </div>
      </div>
      {widthNum > 0 && (
        <div style={{ background: V.bg3, borderRadius: '8px', padding: '16px', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Max Contracts</div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '28px', color: V.goldL, fontWeight: 500 }}>{maxContracts > 0 ? maxContracts : '—'}</div>
          </div>
          {creditNum > 0 && maxContracts > 0 && (
            <div>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Total Premium</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '28px', color: V.sage, fontWeight: 500 }}>${totalPremium.toFixed(0)}</div>
            </div>
          )}
          {maxContracts > 0 && (
            <div>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Max Risk</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '28px', color: V.rose, fontWeight: 500 }}>${actualRisk > 0 ? actualRisk.toFixed(0) : (maxContracts * widthNum * 100).toFixed(0)}</div>
            </div>
          )}
        </div>
      )}
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim, marginTop: '12px' }}>
        Based on {perTradeNum}% max per trade{baselineNum > 0 ? ` · $${Math.round(baselineNum * perTradeNum / 100).toLocaleString()} limit on $${Math.round(baselineNum).toLocaleString()} buying power` : ''}
      </div>
      {onNavigate && (
        <button onClick={() => onNavigate('calc_position')}
          style={{ marginTop: '14px', background: 'transparent', border: `0.5px solid ${V.border}`, borderRadius: '8px', padding: '9px 16px', color: V.muted, fontFamily: "'Jost',sans-serif", fontSize: '12px', cursor: 'pointer', letterSpacing: '0.06em' }}>
          Open Full Position Size Calculator →
        </button>
      )}
    </div>
  );
}

function RiskDashboard({ state, onUpdate, onNavigate }) {
  const lsGet = (k, fb) => { try { const v = localStorage.getItem(k); return v !== null ? JSON.parse(v) : fb; } catch { return fb; } };
  const lsSet = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
  const fmtD   = (n) => '$' + Math.round(Math.abs(n)).toLocaleString();
  const fmtPct = (n) => (n >= 0 ? '+' : '') + n.toFixed(1) + '%';
  const _todayD    = new Date();
  const todayKey   = _todayD.getFullYear() + '-' + String(_todayD.getMonth() + 1).padStart(2, '0') + '-' + String(_todayD.getDate()).padStart(2, '0');
  const todayLabel = _todayD.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const ACCT_TYPES = [
    { id: 'cash',      label: 'Cash Account',     desc: 'Settle trades T+1. No margin. Great for most options sellers.' },
    { id: 'margin',    label: 'Margin Account',   desc: 'Buying power resets faster. More flexibility. Higher risk.' },
    { id: 'portfolio', label: 'Portfolio Margin', desc: 'Risk-based margin. Advanced accounts over $100K.' },
  ];

  const [setupDone,     setSetupDone]     = useState(() => lsGet('tlaw_risk_setup', false));
  const [step,          setStep]          = useState(1);
  const [accountType,   setAccountType]   = useState(() => lsGet('tlaw_risk_accountType', 'cash'));
  const [baseline,      setBaseline]      = useState(() => { const v = lsGet('tlaw_risk_baseline', null); return v !== null ? String(v) : String(state.accountSize || state.currentBalance || ''); });
  const [dailyMax,      setDailyMax]      = useState(() => String(lsGet('tlaw_risk_dailyMax',  state.dailyMaxLoss    || 5)));
  const [weeklyMax,     setWeeklyMax]     = useState(() => String(lsGet('tlaw_risk_weeklyMax', state.weeklyMaxLoss   || 10)));
  const [perTrade,      setPerTrade]      = useState(() => String(lsGet('tlaw_risk_perTrade',  state.maxRiskPerTrade || 2)));
  const [mode,          setMode]          = useState(() => lsGet('tlaw_risk_mode', 'basic'));
  const [dailyLogs,     setDailyLogs]     = useState(() => lsGet('tlaw_risk_dailyLogs', {}));
  const [showSettings,  setShowSettings]  = useState(false);
  const [personalFloor, setPersonalFloor] = useState(() => {
    const pf = lsGet('tlaw_risk_floor', null);
    if (pf !== null) return String(pf);
    const bl = parseFloat(lsGet('tlaw_risk_baseline', 0)) || 0;
    return bl > 0 ? String(Math.round(bl * 0.8)) : '';
  });
  const [savedMsg, setSavedMsg] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const prevFieldVal = useRef('');
  const [logSaved, setLogSaved] = useState(false);
  const [openingBalance, setOpeningBalance] = useState(() => {
    const logs = JSON.parse(localStorage.getItem('tlaw_risk_daily_logs') || '[]');
    const todayEntry = logs.find(log => log.date === todayKey);
    return todayEntry ? String(todayEntry.openingBalance !== undefined ? todayEntry.openingBalance : '') : '';
  });
  const [todayPnl, setTodayPnl] = useState(() => {
    const logs = JSON.parse(localStorage.getItem('tlaw_risk_daily_logs') || '[]');
    const todayEntry = logs.find(log => log.date === todayKey);
    return todayEntry ? String(todayEntry.pnl !== undefined ? todayEntry.pnl : '') : '';
  });
  const [notes, setNotes] = useState(() => {
    const logs = JSON.parse(localStorage.getItem('tlaw_risk_daily_logs') || '[]');
    const todayEntry = logs.find(log => log.date === todayKey);
    return todayEntry ? (todayEntry.notes || '') : '';
  });

  useEffect(() => {
    if (!showSettings) return;
    setAccountType(lsGet('tlaw_risk_accountType', 'cash'));
    const bl = lsGet('tlaw_risk_baseline', null);
    setBaseline(bl !== null ? String(bl) : String(state.accountSize || state.currentBalance || ''));
    setDailyMax(String(lsGet('tlaw_risk_dailyMax',  state.dailyMaxLoss    || 5)));
    setWeeklyMax(String(lsGet('tlaw_risk_weeklyMax', state.weeklyMaxLoss   || 10)));
    setPerTrade(String(lsGet('tlaw_risk_perTrade',  state.maxRiskPerTrade || 2)));
    setMode(lsGet('tlaw_risk_mode', 'basic'));
    const pf = lsGet('tlaw_risk_floor', null);
    if (pf !== null) {
      setPersonalFloor(String(pf));
    } else {
      const blNum = parseFloat(lsGet('tlaw_risk_baseline', 0)) || 0;
      setPersonalFloor(blNum > 0 ? String(Math.round(blNum * 0.8)) : '');
    }
    setSavedMsg(false);
  }, [showSettings]); // eslint-disable-line

  useEffect(() => {
    const logs = JSON.parse(localStorage.getItem('tlaw_risk_daily_logs') || '[]');
    if (logs.find(log => log.date === todayKey)) setLogSaved(true);
  }, []); // eslint-disable-line

  const balanceLabel  = accountType === 'cash' ? 'Available Cash' : accountType === 'portfolio' ? 'Available Dollars' : 'Options Buying Power';
  const balanceHelper = accountType === 'cash'
    ? 'In ThinkorSwim: Monitor tab → Balances → Cash & Sweep Vehicle'
    : accountType === 'portfolio'
      ? 'In ThinkorSwim: Monitor tab → Balances → Available Dollars'
      : 'In ThinkorSwim: Monitor tab → Balances → Options Buying Power';

  const todayLog = dailyLogs[todayKey] || { openBalance: '', pnl: '', notes: '' };
  const updateTodayLog = (patch) => {
    const next = { ...dailyLogs, [todayKey]: { ...todayLog, ...patch } };
    setDailyLogs(next);
    lsSet('tlaw_risk_dailyLogs', next);
  };

  const baselineNum       = parseFloat(baseline)      || 0;
  const dailyMaxNum       = parseFloat(dailyMax)       || 5;
  const weeklyMaxNum      = parseFloat(weeklyMax)      || 10;
  const perTradeNum       = parseFloat(perTrade)       || 2;
  const personalFloorNum  = parseFloat(personalFloor)  || 0;
  const openBal           = parseFloat(openingBalance) || baselineNum;
  const dailyFloor        = personalFloorNum > 0 ? personalFloorNum : openBal * (1 - dailyMaxNum / 100);
  const dailyFloorIsPersonal = personalFloorNum > 0;
  const weeklyFloor       = baselineNum * (1 - weeklyMaxNum / 100);
  const maxPerTradeDollar = openBal * perTradeNum / 100;
  const todayPnlNum       = parseFloat(todayPnl) || 0;
  const todayPnlPct       = openBal > 0 ? (todayPnlNum / openBal * 100) : 0;
  const dailyLossUsedPct  = openBal > 0 ? Math.min(dailyMaxNum, Math.abs(Math.min(0, todayPnlNum)) / openBal * 100) : 0;
  const dailyRemainingDollar = Math.max(0, openBal - dailyFloor - Math.abs(Math.min(0, todayPnlNum)));

  const completeSetup = () => {
    lsSet('tlaw_risk_setup', true);
    lsSet('tlaw_risk_accountType', accountType);
    lsSet('tlaw_risk_baseline', parseFloat(baseline) || 0);
    lsSet('tlaw_risk_dailyMax',  parseFloat(dailyMax)  || 5);
    lsSet('tlaw_risk_weeklyMax', parseFloat(weeklyMax) || 10);
    lsSet('tlaw_risk_perTrade',  parseFloat(perTrade)  || 2);
    lsSet('tlaw_risk_floor', parseFloat(personalFloor) || Math.round(baselineNum * 0.8));
    lsSet('tlaw_risk_mode', mode);
    setSetupDone(true);
  };

  const saveSettings = () => {
    lsSet('tlaw_risk_accountType', accountType);
    lsSet('tlaw_risk_baseline', parseFloat(baseline) || 0);
    lsSet('tlaw_risk_dailyMax',  parseFloat(dailyMax)  || 5);
    lsSet('tlaw_risk_weeklyMax', parseFloat(weeklyMax) || 10);
    lsSet('tlaw_risk_perTrade',  parseFloat(perTrade)  || 2);
    lsSet('tlaw_risk_floor', parseFloat(personalFloor) || Math.round(baselineNum * 0.8));
    lsSet('tlaw_risk_mode', mode);
    setSavedMsg(true);
    setTimeout(() => { setSavedMsg(false); setShowSettings(false); }, 2000);
  };

  const cancelSettings = () => {
    setAccountType(lsGet('tlaw_risk_accountType', 'cash'));
    const bl = lsGet('tlaw_risk_baseline', null);
    setBaseline(bl !== null ? String(bl) : String(state.accountSize || state.currentBalance || ''));
    setDailyMax(String(lsGet('tlaw_risk_dailyMax',  state.dailyMaxLoss    || 5)));
    setWeeklyMax(String(lsGet('tlaw_risk_weeklyMax', state.weeklyMaxLoss   || 10)));
    setPerTrade(String(lsGet('tlaw_risk_perTrade',  state.maxRiskPerTrade || 2)));
    setMode(lsGet('tlaw_risk_mode', 'basic'));
    const pf = lsGet('tlaw_risk_floor', null);
    if (pf !== null) {
      setPersonalFloor(String(pf));
    } else {
      const blNum = parseFloat(lsGet('tlaw_risk_baseline', 0)) || 0;
      setPersonalFloor(blNum > 0 ? String(Math.round(blNum * 0.8)) : '');
    }
    setEditingField(null);
    setShowSettings(false);
  };

  const getWeekDays = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun 1=Mon...6=Sat
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysFromMonday);
    const days = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const k = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      const log = dailyLogs[k] || {};
      const ob = log.openBalance !== '' && log.openBalance !== undefined ? parseFloat(log.openBalance) : null;
      const isFuture = d > today && k !== todayKey;
      days.push({ key: k, label: d.toLocaleDateString('en-US', { weekday: 'short' }), date: d.getDate(), openBalance: ob, isFuture });
    }
    return days;
  };
  const weekDays = getWeekDays();

  const rd = {
    wrap:         { background: V.bg, minHeight: '100vh', padding: '32px 20px 60px' },
    inner:        { maxWidth: '760px', margin: '0 auto' },
    card2:        { background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '22px 24px' },
    lbl:          { fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '6px' },
    inp:          { width: '100%', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px', padding: '10px 14px', color: V.text, fontSize: '14px', fontFamily: "'Jost',sans-serif", outline: 'none', boxSizing: 'border-box' },
    btnPrimary:   { padding: '13px 20px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '8px', color: V.goldL, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '13px', letterSpacing: '0.08em', fontWeight: 500 },
    btnSecondary: { padding: '11px 16px', background: 'transparent', border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.muted, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '13px' },
  };

  // ── SETUP WIZARD ──
  if (!setupDone) {
    const stepTitles = ['Account Type', balanceLabel, 'Dashboard Mode', 'Risk Rules', 'Review & Confirm'];
    const canAdvance = !(step === 2 && (!baseline || parseFloat(baseline) <= 0));
    return (
      <div style={rd.wrap}>
        <div style={{ ...rd.inner, maxWidth: '520px' }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', justifyContent: 'center' }}>
            {[1,2,3,4,5].map(n => (
              <div key={n} style={{ height: '5px', borderRadius: '3px', transition: 'all 0.3s', background: n <= step ? V.gold : V.bg3, width: n === step ? '28px' : '14px' }} />
            ))}
          </div>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: V.muted, marginBottom: '8px', textAlign: 'center' }}>Step {step} of 5</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '30px', fontWeight: 500, color: V.text, marginBottom: '28px', textAlign: 'center' }}>{stepTitles[step-1]}</h2>

          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {ACCT_TYPES.map(t => (
                <div key={t.id} onClick={() => setAccountType(t.id)}
                  style={{ background: accountType === t.id ? 'rgba(201,168,76,0.08)' : V.bg2, border: `1px solid ${accountType === t.id ? 'rgba(201,168,76,0.4)' : V.border}`, borderRadius: '12px', padding: '18px 20px', cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: '14px', color: accountType === t.id ? V.goldL : V.text, marginBottom: '4px' }}>{t.label}</div>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted }}>{t.desc}</div>
                </div>
              ))}
            </div>
          )}

          {step === 2 && (
            <div>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, marginBottom: '20px' }}>
                <span style={{ color: V.text }}>{balanceHelper}.</span> Enter today's value — this becomes your position sizing baseline.
              </p>
              <div style={rd.lbl}>{balanceLabel} ($)</div>
              <input style={rd.inp} type="number" value={baseline} onChange={e => setBaseline(e.target.value)} placeholder="e.g. 25000" />
              {parseFloat(baseline) > 0 && (
                <div style={{ marginTop: '14px', background: V.bg3, borderRadius: '8px', padding: '14px 16px' }}>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, marginBottom: '6px' }}>With current defaults:</div>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text }}>
                    Daily stop: <span style={{ color: V.rose }}>{fmtD(parseFloat(baseline) * 0.05)}</span> &nbsp;&middot;&nbsp;
                    Per trade: <span style={{ color: V.goldL }}>{fmtD(parseFloat(baseline) * 0.02)}</span> &nbsp;&middot;&nbsp;
                    Weekly stop: <span style={{ color: '#c084fc' }}>{fmtD(parseFloat(baseline) * 0.10)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { id: 'basic',    label: 'Basic Mode',    desc: 'See your risk rules and limits each session. Clean and direct.' },
                { id: 'advanced', label: 'Advanced Mode', desc: 'Morning balance log + weekly pattern view. Track patterns over time.' },
              ].map(m => (
                <div key={m.id} onClick={() => setMode(m.id)}
                  style={{ background: mode === m.id ? 'rgba(201,168,76,0.08)' : V.bg2, border: `1px solid ${mode === m.id ? 'rgba(201,168,76,0.4)' : V.border}`, borderRadius: '12px', padding: '18px 20px', cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: '14px', color: mode === m.id ? V.goldL : V.text, marginBottom: '4px' }}>{m.label}</div>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted }}>{m.desc}</div>
                </div>
              ))}
            </div>
          )}

          {step === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {[
                { k: 'dailyMax',  lbl: 'Daily Max Loss (%)',     val: dailyMax,  set: setDailyMax,  min: 1,   max: 10, note: 'Recommended: 5%'  },
                { k: 'weeklyMax', lbl: 'Weekly Max Loss (%)',    val: weeklyMax, set: setWeeklyMax, min: 3,   max: 20, note: 'Recommended: 10%' },
                { k: 'perTrade',  lbl: 'Max Risk Per Trade (%)', val: perTrade,  set: setPerTrade,  min: 0.5, max: 5,  note: 'Recommended: 2%'  },
              ].map(f => (
                <div key={f.k}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={rd.lbl}>{f.lbl}</div>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim }}>{f.note}</span>
                  </div>
                  <input style={rd.inp} type="number" value={f.val} onChange={e => f.set(e.target.value)} min={f.min} max={f.max} step="0.5" />
                  {parseFloat(f.val) > 0 && baselineNum > 0 && (
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim, marginTop: '4px' }}>
                      = {fmtD(baselineNum * parseFloat(f.val) / 100)} on your baseline
                    </div>
                  )}
                </div>
              ))}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div style={rd.lbl}>Personal Floor ($)</div>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim }}>Suggested: 80% of baseline</span>
                </div>
                <input style={rd.inp} type="number" value={personalFloor} onChange={e => setPersonalFloor(e.target.value)} placeholder={baselineNum > 0 ? String(Math.round(baselineNum * 0.8)) : 'e.g. 20000'} />
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim, marginTop: '4px', lineHeight: 1.6 }}>
                  The minimum balance you will never trade below.{baselineNum > 0 ? ` Suggested: $${Math.round(baselineNum * 0.8).toLocaleString()} based on your entry above.` : ''}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div style={{ ...rd.card2 }}>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, marginBottom: '16px' }}>Your Risk Profile</div>
              {[
                { l: 'Account Type',   v: ACCT_TYPES.find(a => a.id === accountType)?.label || accountType },
                { l: balanceLabel,     v: fmtD(parseFloat(baseline) || 0) },
                { l: 'Daily Max Loss', v: `${dailyMax}% = ${fmtD((parseFloat(baseline)||0) * (parseFloat(dailyMax)||5)   / 100)}` },
                { l: 'Weekly Max Loss', v: `${weeklyMax}% = ${fmtD((parseFloat(baseline)||0) * (parseFloat(weeklyMax)||10) / 100)}` },
                { l: 'Max Per Trade',  v: `${perTrade}% = ${fmtD((parseFloat(baseline)||0) * (parseFloat(perTrade)||2)   / 100)}` },
                { l: 'Personal Floor', v: personalFloor ? fmtD(parseFloat(personalFloor)) : `$${Math.round((parseFloat(baseline)||0) * 0.8).toLocaleString()} (default)` },
                { l: 'Dashboard Mode', v: mode === 'advanced' ? 'Advanced (with logging)' : 'Basic' },
              ].map(r => (
                <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `0.5px solid ${V.border}` }}>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted }}>{r.l}</span>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text, fontWeight: 500 }}>{r.v}</span>
                </div>
              ))}
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim, marginTop: '14px', lineHeight: 1.7 }}>
                You can update these any time from Settings inside your dashboard.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '28px' }}>
            {step > 1 && (
              <button onClick={() => setStep(s => s - 1)} style={{ ...rd.btnSecondary, flex: 1 }}>&#8592; Back</button>
            )}
            <button onClick={() => step < 5 ? setStep(s => s + 1) : completeSetup()} disabled={!canAdvance}
              style={{ ...rd.btnPrimary, flex: 2, opacity: canAdvance ? 1 : 0.4, cursor: canAdvance ? 'pointer' : 'not-allowed' }}>
              {step === 5 ? 'Launch My Dashboard →' : 'Continue →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── SETTINGS PANEL ──
  if (showSettings) {
    const renderClickEdit = (field, value, setValue, displayVal) => {
      const isEditing = editingField === field;
      if (isEditing) {
        return (
          <input autoFocus style={rd.inp} type="number" value={value}
            onChange={e => setValue(e.target.value)}
            onBlur={e => { if (!e.target.value.trim()) setValue(prevFieldVal.current); setEditingField(null); }}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
          />
        );
      }
      return (
        <div onClick={() => { prevFieldVal.current = value; setEditingField(field); }}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px', cursor: 'pointer' }}>
          <span style={{ flex: 1, fontFamily: "'Jost',sans-serif", fontSize: '14px', color: V.text }}>{displayVal}</span>
          <span style={{ color: V.dim, fontSize: '14px', lineHeight: 1 }}>✎</span>
        </div>
      );
    };

    return (
      <div style={rd.wrap}>
        <div style={{ ...rd.inner, maxWidth: '520px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
            <button onClick={cancelSettings} style={{ background: 'none', border: 'none', color: V.muted, cursor: 'pointer', fontSize: '18px', padding: 0 }}>&#8592;</button>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '26px', fontWeight: 500, color: V.text, margin: 0 }}>Risk Settings</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

            {/* Account Type */}
            <div>
              <div style={rd.lbl}>Account Type</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {ACCT_TYPES.map(t => (
                  <button key={t.id} onClick={() => setAccountType(t.id)}
                    style={{ flex: 1, padding: '10px 6px', background: accountType === t.id ? 'rgba(201,168,76,0.1)' : V.bg3, border: `1px solid ${accountType === t.id ? 'rgba(201,168,76,0.35)' : V.border}`, borderRadius: '8px', color: accountType === t.id ? V.goldL : V.muted, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '12px' }}>
                    {t.id === 'cash' ? 'Cash' : t.id === 'margin' ? 'Margin' : 'Portfolio'}
                  </button>
                ))}
              </div>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim, marginTop: '8px', fontStyle: 'italic', lineHeight: 1.6 }}>
                Select your account type. This changes how your baseline is labeled throughout the dashboard.
              </div>
            </div>

            {/* Baseline */}
            <div>
              <div style={rd.lbl}>{balanceLabel} Baseline ($)</div>
              {renderClickEdit('baseline', baseline, setBaseline,
                parseFloat(baseline) > 0 ? '$' + Math.round(parseFloat(baseline)).toLocaleString() : '—')}
            </div>

            {/* Percentage fields */}
            {[
              { k: 'dailyMax',  lbl: 'Daily Max Loss (%)',     val: dailyMax,  set: setDailyMax  },
              { k: 'weeklyMax', lbl: 'Weekly Max Loss (%)',    val: weeklyMax, set: setWeeklyMax },
              { k: 'perTrade',  lbl: 'Max Risk Per Trade (%)', val: perTrade,  set: setPerTrade  },
            ].map(f => (
              <div key={f.k}>
                <div style={rd.lbl}>{f.lbl}</div>
                {renderClickEdit(f.k, f.val, f.set, f.val ? f.val + '%' : '—')}
                {parseFloat(f.val) > 0 && parseFloat(baseline) > 0 && (
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim, marginTop: '4px' }}>= {fmtD(parseFloat(baseline) * parseFloat(f.val) / 100)}</div>
                )}
              </div>
            ))}

            {/* Personal Floor */}
            <div>
              <div style={rd.lbl}>Personal Floor ($)</div>
              {renderClickEdit('personalFloor', personalFloor, setPersonalFloor,
                parseFloat(personalFloor) > 0 ? '$' + Math.round(parseFloat(personalFloor)).toLocaleString() : (baselineNum > 0 ? '$' + Math.round(baselineNum * 0.8).toLocaleString() + ' (default)' : '—'))}
              {baselineNum > 0 && (
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim, marginTop: '4px' }}>
                  Suggested: ${Math.round(baselineNum * 0.8).toLocaleString()} (80% of baseline)
                </div>
              )}
            </div>

            {/* Dashboard Mode */}
            <div>
              <div style={rd.lbl}>Dashboard Mode</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[['basic','Basic'],['advanced','Advanced']].map(([id,ml]) => (
                  <button key={id} onClick={() => setMode(id)}
                    style={{ flex: 1, padding: '10px', background: mode === id ? 'rgba(201,168,76,0.1)' : V.bg3, border: `1px solid ${mode === id ? 'rgba(201,168,76,0.35)' : V.border}`, borderRadius: '8px', color: mode === id ? V.goldL : V.muted, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '13px' }}>{ml}</button>
                ))}
              </div>
            </div>

            {savedMsg && (
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.sage, textAlign: 'center', letterSpacing: '0.04em' }}>✓ Settings saved</div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '10px', paddingTop: '8px' }}>
              <button onClick={saveSettings} style={{ ...rd.btnPrimary, flex: 2 }}>Save Changes</button>
              <button onClick={cancelSettings} style={{ ...rd.btnSecondary, flex: 1 }}>Cancel</button>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => {
                if (window.confirm('Reset all settings to defaults?\nCurrent settings will be lost.')) {
                  const blNum = parseFloat(baseline) || 0;
                  setDailyMax('5'); setWeeklyMax('10'); setPerTrade('2');
                  setPersonalFloor(blNum > 0 ? String(Math.round(blNum * 0.8)) : '');
                  setMode('basic');
                  lsSet('tlaw_risk_dailyMax', 5); lsSet('tlaw_risk_weeklyMax', 10); lsSet('tlaw_risk_perTrade', 2);
                  lsSet('tlaw_risk_floor', blNum > 0 ? Math.round(blNum * 0.8) : 0);
                  lsSet('tlaw_risk_mode', 'basic');
                  setSavedMsg(true);
                  setTimeout(() => { setSavedMsg(false); setShowSettings(false); }, 2000);
                }
              }} style={{ background: 'none', border: 'none', color: V.rose, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '12px', padding: '4px 0', letterSpacing: '0.04em' }}>Reset to Defaults</button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ── MAIN DASHBOARD ──
  const recentLogs = Object.entries(dailyLogs)
    .filter(([, v]) => v.openBalance || v.pnl)
    .sort(([a], [b]) => b.localeCompare(a))
    .slice(0, 5);

  return (
    <div style={rd.wrap}>
      <div style={rd.inner}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: V.muted, marginBottom: '6px' }}>&#x25C8; Risk Dashboard</div>
            <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '32px', fontWeight: 500, color: V.text, margin: 0 }}>Today's Risk Snapshot</h1>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.dim, marginTop: '4px' }}>{todayLabel} &middot; {ACCT_TYPES.find(a => a.id === accountType)?.label}</div>
            <div style={{ marginTop: '6px', fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim, fontStyle: 'italic' }}>
              {mode === 'advanced' ? 'Advanced Mode' : 'Basic Mode'} &mdash;{' '}
              <button onClick={() => { const m = mode === 'advanced' ? 'basic' : 'advanced'; setMode(m); lsSet('tlaw_risk_mode', m); }}
                style={{ background: 'none', border: 'none', color: V.muted, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '11px', fontStyle: 'italic', padding: 0, textDecoration: 'underline', textDecorationColor: 'rgba(240,237,230,0.25)' }}>
                {mode === 'advanced' ? 'Switch to Basic →' : 'Switch to Advanced →'}
              </button>
            </div>
          </div>
          <button onClick={() => setShowSettings(true)} style={{ ...rd.btnSecondary, fontSize: '12px', letterSpacing: '0.06em', padding: '8px 14px', alignSelf: 'flex-start' }}>&#9881; Settings</button>
        </div>

        {/* Snapshot tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: '10px', marginBottom: '18px' }}>
          {/* Opening Balance */}
          {[
            { lbl: 'Opening Balance', val: openingBalance ? '$' + Math.round(parseFloat(openingBalance)).toLocaleString() : baselineNum > 0 ? '$' + Math.round(baselineNum).toLocaleString() : 'Log below to begin', sub: openingBalance ? "Today's logged balance" : 'From your setup baseline', color: V.goldL },
          ].map(s => (
            <div key={s.lbl} style={{ ...rd.card2, padding: '18px' }}>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, marginBottom: '8px' }}>{s.lbl}</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '24px', fontWeight: 500, color: s.color }}>{s.val}</div>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim, marginTop: '4px' }}>{s.sub}</div>
            </div>
          ))}
          {/* Daily Stop */}
          <div style={{ background: 'rgba(196,122,122,0.08)', border: `0.5px solid rgba(196,122,122,0.3)`, borderRadius: '10px', padding: '16px' }}>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.rose, marginBottom: '8px' }}>Daily Stop</div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '22px', fontWeight: 700, color: V.rose }}>{baselineNum > 0 ? '$' + Math.round(baselineNum * dailyMaxNum / 100).toLocaleString() : '—'}</div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, marginTop: '4px' }}>{dailyMax}% of baseline — stop all trading if you hit this</div>
          </div>
          {/* Personal/Daily Floor, Weekly Floor, Max Per Trade */}
          {[
            { lbl: dailyFloorIsPersonal ? 'Personal Floor' : 'Daily Floor', val: dailyFloor > 0 ? '$' + Math.round(dailyFloor).toLocaleString() : '—', sub: dailyFloorIsPersonal ? 'Never trade below this' : `Set floor in Settings for accuracy`, color: V.rose },
            { lbl: 'Weekly Floor',    val: baselineNum > 0 ? '$' + Math.round(weeklyFloor).toLocaleString()  : '—', sub: `Step back at ${weeklyMax}% weekly`,    color: '#c084fc' },
            { lbl: 'Max Per Trade',   val: openBal > 0     ? '$' + Math.round(maxPerTradeDollar).toLocaleString() : '—', sub: `${perTrade}% of buying power`,    color: V.sky     },
          ].map(s => (
            <div key={s.lbl} style={{ ...rd.card2, padding: '18px' }}>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, marginBottom: '8px' }}>{s.lbl}</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '24px', fontWeight: 500, color: s.color }}>{s.val}</div>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim, marginTop: '4px' }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Daily loss meter */}
        {openBal > 0 && (
          <div style={{ ...rd.card2, marginBottom: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted }}>Daily Loss Meter</div>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: dailyLossUsedPct >= dailyMaxNum ? V.rose : V.goldL }}>
                {dailyLossUsedPct.toFixed(1)}% of {dailyMax}% limit used
              </div>
            </div>
            <div style={{ background: V.bg3, borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: '4px', transition: 'width 0.4s ease', width: `${Math.min(100, (dailyLossUsedPct / dailyMaxNum) * 100)}%`, background: dailyLossUsedPct >= dailyMaxNum ? V.rose : dailyLossUsedPct >= dailyMaxNum * 0.75 ? '#f59e0b' : V.goldL }} />
            </div>
            {(() => {
              if (dailyRemainingDollar <= 0) return (
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.rose, marginTop: '8px' }}>⚠ Daily stop reached — no new trades today</div>
              );
              const dailyStopDollar = openBal * dailyMaxNum / 100;
              const remainRatio = dailyStopDollar > 0 ? dailyRemainingDollar / dailyStopDollar : 1;
              const remColor = remainRatio > 0.5 ? V.sage : remainRatio > 0.25 ? V.gold : V.rose;
              return (
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: remColor, marginTop: '8px' }}>
                  ${Math.round(dailyRemainingDollar).toLocaleString()} remaining before daily stop
                </div>
              );
            })()}
          </div>
        )}

        {/* Morning Log + Recent Logs — Advanced only */}
        {mode === 'advanced' && <div style={{ ...rd.card2, marginBottom: '18px' }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, marginBottom: '16px' }}>
            Morning Log &mdash; {todayLabel}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <div style={rd.lbl}>Opening Balance ($)</div>
              <input style={rd.inp} type="number" value={openingBalance} onChange={e => setOpeningBalance(e.target.value)} placeholder={baseline || '0'} />
            </div>
            <div>
              <div style={rd.lbl}>Today's P&amp;L ($)</div>
              <input style={rd.inp} type="number" value={todayPnl} onChange={e => setTodayPnl(e.target.value)} placeholder="e.g. -150 or 320" />
            </div>
          </div>
          <div>
            <div style={rd.lbl}>Notes</div>
            <textarea style={{ ...rd.inp, resize: 'vertical', minHeight: '72px', lineHeight: 1.7 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Market conditions, setups watched, lessons learned..." />
          </div>
          {logSaved && todayPnl !== '' && parseFloat(todayPnl) !== 0 && (
            <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '8px', borderLeft: `2px solid ${parseFloat(todayPnl) >= 0 ? V.sage : V.rose}`, background: parseFloat(todayPnl) >= 0 ? 'rgba(122,171,138,0.06)' : 'rgba(196,122,122,0.06)' }}>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: parseFloat(todayPnl) >= 0 ? V.sage : V.rose }}>
                {parseFloat(todayPnl) >= 0 ? '▲ ' : '▼ '}{fmtD(Math.abs(parseFloat(todayPnl)))}{openBal > 0 ? ` (${fmtPct(todayPnlPct)})` : ''}
              </span>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, marginLeft: '10px' }}>
                {parseFloat(todayPnl) >= 0 ? 'Profitable session' : dailyLossUsedPct >= dailyMaxNum ? '⚠ Daily stop hit' : 'Within daily limit'}
              </span>
            </div>
          )}

          <button
            style={{ width: '100%', padding: '12px', marginTop: '12px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '8px', color: V.goldL, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '13px', letterSpacing: '0.08em', fontWeight: 500 }}
            onClick={() => {
              const entry = { date: todayKey, openingBalance: Number(openingBalance), pnl: Number(todayPnl), notes, timestamp: Date.now() };
              const logs = JSON.parse(localStorage.getItem('tlaw_risk_daily_logs') || '[]');
              const existingIndex = logs.findIndex(l => l.date === todayKey);
              if (existingIndex >= 0) { logs[existingIndex] = entry; } else { logs.push(entry); }
              localStorage.setItem('tlaw_risk_daily_logs', JSON.stringify(logs));
              const next = { ...dailyLogs, [todayKey]: { openBalance: openingBalance, pnl: todayPnl, notes } };
              setDailyLogs(next);
              lsSet('tlaw_risk_dailyLogs', next);
              setLogSaved(true);
            }}>
            Save Today's Log →
          </button>
          {logSaved && (
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.sage, marginTop: '8px', letterSpacing: '0.02em' }}>
              ✓ Logged for {new Date(todayKey + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
            </div>
          )}

          {/* Recent Logs */}
          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `0.5px solid ${V.border}` }}>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, marginBottom: '12px' }}>Recent Logs</div>
            {recentLogs.length === 0 ? (
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim, fontStyle: 'italic', margin: 0 }}>No entries yet. Log your first morning balance above.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Jost',sans-serif", fontSize: '12px' }}>
                  <thead>
                    <tr>
                      {['Date', 'Opening Balance', 'P&L', 'Notes'].map(h => (
                        <th key={h} style={{ textAlign: 'left', color: V.dim, fontWeight: 500, paddingBottom: '8px', paddingRight: '12px', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentLogs.map(([date, log]) => {
                      const pnlNum = parseFloat(log.pnl) || 0;
                      return (
                        <tr key={date} style={{ borderTop: `0.5px solid rgba(240,237,230,0.05)` }}>
                          <td style={{ padding: '8px 12px 8px 0', color: V.muted, whiteSpace: 'nowrap' }}>{new Date(date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                          <td style={{ padding: '8px 12px 8px 0', color: V.text }}>{log.openBalance ? '$' + Math.round(parseFloat(log.openBalance)).toLocaleString() : '—'}</td>
                          <td style={{ padding: '8px 12px 8px 0', color: pnlNum > 0 ? V.sage : pnlNum < 0 ? V.rose : V.muted, whiteSpace: 'nowrap' }}>{log.pnl ? (pnlNum >= 0 ? '+' : '') + '$' + Math.round(Math.abs(pnlNum)).toLocaleString() : '—'}</td>
                          <td style={{ padding: '8px 0', color: V.dim, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.notes || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>}

        {/* Weekly Pattern — Advanced only */}
        {mode === 'advanced' && <div style={{ ...rd.card2, marginBottom: '18px' }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.gold, marginBottom: '20px' }}>
            This Week's Pattern
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            {weekDays.map(d => {
              const has      = !d.isFuture && d.openBalance !== null && !isNaN(d.openBalance) && d.openBalance > 0;
              const isToday  = d.key === todayKey;
              const floor    = dailyFloor || baselineNum * 0.8;
              const aboveFloor = has && floor > 0 ? (d.openBalance - floor) / floor : null;
              const barColor = d.isFuture ? 'rgba(240,237,230,0.04)'
                : !has ? V.bg3
                : aboveFloor >= 0.15 ? V.sage
                : aboveFloor >= 0.05 ? V.gold
                : V.rose;
              const barH = has ? 80 : 20;
              return (
                <div key={d.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '100%', height: barH + 'px', background: barColor, borderRadius: '4px 4px 0 0', opacity: d.isFuture ? 0.3 : isToday ? 1 : 0.7, outline: isToday ? `1px solid ${barColor}` : 'none', outlineOffset: '2px', transition: 'all 0.3s' }} />
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: isToday ? V.goldL : d.isFuture ? 'rgba(240,237,230,0.2)' : V.dim, fontWeight: isToday ? 600 : 400 }}>{d.label}</div>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: has ? V.muted : V.dim }}>
                    {has ? '$' + Math.round(d.openBalance / 1000).toFixed(0) + 'k' : '—'}
                  </div>
                </div>
              );
            })}
          </div>
          {(() => {
            const logged = weekDays.filter(d => d.openBalance !== null && d.openBalance > 0);
            const floor  = dailyFloor || baselineNum * 0.8;
            const allGreen = logged.length === 5 && logged.every(d => floor > 0 && (d.openBalance - floor) / floor >= 0.15);
            const trending = logged.length >= 3 && logged[logged.length - 1]?.openBalance < logged[0]?.openBalance;
            let msg = 'Start logging your morning balance to see patterns in your risk usage over time.';
            if (logged.length > 0 && logged.length < 5) msg = 'Log your opening balance each morning to see your weekly pattern.';
            if (logged.length === 5 && allGreen) msg = 'Consistent week — well within your risk limits.';
            else if (logged.length >= 3 && trending) msg = 'Balance declining through the week. Review your position sizing.';
            return (
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.dim, fontStyle: 'italic', marginTop: '14px', marginBottom: 0, lineHeight: 1.7 }}>{msg}</p>
            );
          })()}
          <div style={{ display: 'flex', gap: '14px', marginTop: '12px', flexWrap: 'wrap' }}>
            {[['> 15% above floor', V.sage], ['5–15% above floor', V.gold], ['< 5% above floor', V.rose], ['No log', V.bg3]].map(([lbl, c]) => (
              <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: c }} />
                <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.dim }}>{lbl}</span>
              </div>
            ))}
          </div>
        </div>}

        {/* Position Sizing Quick Calculator */}
        <PositionSizingMini baseline={openBal || baselineNum} perTradeNum={perTradeNum} onNavigate={onNavigate} />

        {/* Active Rules */}
        <div style={{ ...rd.card2, borderLeft: `3px solid ${V.gold}` }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.goldL, marginBottom: '12px' }}>Your Active Rules</div>
          {[
            `Daily Max Loss: ${dailyMax}% = $${Math.round(openBal * dailyMaxNum / 100).toLocaleString()} — stop all trading if you hit this`,
            `Weekly Max Loss: ${weeklyMax}% = $${Math.round(baselineNum * weeklyMaxNum / 100).toLocaleString()} — step back, review, reset next week`,
            `Max Per Trade: ${perTrade}% = $${Math.round(openBal * perTradeNum / 100).toLocaleString()} — never exceed this on any single position`,
            ...(personalFloorNum > 0 ? [`Personal Floor: $${Math.round(personalFloorNum).toLocaleString()} — never trade below this balance`] : []),
          ].map(r => (
            <div key={r} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '8px' }}>
              <span style={{ color: V.gold, fontSize: '12px', marginTop: '3px', flexShrink: 0 }}>&#x25C8;</span>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.75 }}>{r}</span>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

export default RiskDashboard
